/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { TFunction } from 'i18next';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ReactHookFormDateTime,
  isXHoursBefore,
  useDraftFormHelpers,
} from '@opengeoweb/form-fields';
import { dateUtils } from '@opengeoweb/shared';
import type { ConfigurableFormFieldProps, ProductConfig } from '../../../types';
import { useSigmetAirmetTranslation } from '../../../utils/i18n';
import { styles } from '../ProductForm.styles';
import {
  getFieldLabel,
  getHoursBeforeValidity,
  getValidFromDelayTimeMinutesFromConfig,
  triggerValidations,
} from '../utils';
import ProductFormFieldLayout from './ProductFormFieldLayout';

export const getValidFromXHoursBeforeMessage = (
  t: TFunction,
  hours: number,
): string => t('valid-from-time-error', { hours });

export const getDefaultValidFromValue = (
  productConfig: ProductConfig,
): string => {
  const validFromDelayTimeMinutes =
    getValidFromDelayTimeMinutesFromConfig(productConfig);

  const dateWithDelay = dateUtils.add(dateUtils.utc(), {
    minutes: validFromDelayTimeMinutes,
  });

  const minutes = dateWithDelay.getUTCMinutes();

  // Calculate the minutes needed to round up to the next half hour
  const extraMinutes = minutes <= 30 ? 30 - minutes : 60 - minutes;

  const roundedDate = dateUtils.add(dateWithDelay, { minutes: extraMinutes });
  return dateUtils.dateToString(roundedDate, "yyyy-MM-dd'T'HH:mm:00'Z'")!;
};

interface ValidFromProps extends ConfigurableFormFieldProps {
  lockBecauseActive?: boolean;
}

const ValidFrom: React.FC<ValidFromProps> = ({
  productConfig,
  isDisabled,
  isReadOnly,
  onChange = (): void => {},
  onUserChange = (): void => {},
  lockBecauseActive = false,
}) => {
  const { t } = useSigmetAirmetTranslation();
  const { getValues, setValue, trigger } = useFormContext();
  const { isRequired } = useDraftFormHelpers();
  const label = getFieldLabel(t, t('date-and-time'), isReadOnly!);

  // Only auto-sync to now when amending an already-active SIGMET
  React.useEffect(() => {
    if (!lockBecauseActive) {
      return;
    }

    const updateNow = (): void =>
      setValue('validDateStart', dateUtils.getCurrentTimeAsString(), {
        shouldValidate: true,
      });

    updateNow();

    let intervalId: NodeJS.Timeout | undefined;
    const msUntilNextMinute = 60_000 - (Date.now() % 60_000);
    const timeoutId = setTimeout(() => {
      updateNow();
      intervalId = setInterval(updateNow, 60_000);
    }, msUntilNextMinute);

    // eslint-disable-next-line consistent-return
    return (): void => {
      clearTimeout(timeoutId);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [lockBecauseActive, setValue]);

  const nowIso = dateUtils.getCurrentTimeAsString();
  const rules = lockBecauseActive
    ? {}
    : {
        validate: {
          isRequired,
          isValidDate: dateUtils.isValidIsoDateString,
          isValueBeforeCurrentTime: (value: string): boolean | string =>
            isXHoursBefore(value, nowIso, 0) ||
            t('valid-from-before-current-error'),
          isCurrentTimeXHoursBeforeValue: (value: string): boolean | string => {
            const hours = getHoursBeforeValidity(
              getValues('phenomenon'),
              getValues('locationIndicatorATSR'),
              productConfig,
            );
            return (
              isXHoursBefore(nowIso, value, hours) ||
              getValidFromXHoursBeforeMessage(t, hours)
            );
          },
        },
      };

  return (
    <ProductFormFieldLayout title={t('valid-from')}>
      <ReactHookFormDateTime
        data-testid="valid-from"
        defaultNullValue={getDefaultValidFromValue(productConfig)}
        disablePast
        disabled={isDisabled || lockBecauseActive}
        isReadOnly={isReadOnly}
        label={label}
        name="validDateStart"
        onChange={() => {
          onUserChange?.();
          triggerValidations(
            ['validDateEnd', 'observationOrForecastTime'],
            getValues,
            trigger,
          );
          onChange();
        }}
        rules={rules}
        sx={{
          ...styles.helperText,
          '.MuiFormHelperText-root.Mui-error': { marginBottom: '-16px' },
        }}
      />
    </ProductFormFieldLayout>
  );
};

export default ValidFrom;
